import React, { useState, useEffect } from 'react';
import './Maintenance.scss';
import WorkplaceTable from 'components/Table/WorkplaceTable';
import BasePagination from 'base-components/BasePagination/BasePagination';
import GridColumnsConfigProvider from 'providers/GridColumnsConfigProvider';
import { GridFilterUrlProvider } from 'providers/GridFilterUrlProvider';
import { MdCancel, MdEdit, MdDelete } from 'react-icons/md';
import BaseButton from 'base-components/BaseButton/BaseButton';
import MaintenanceEditModal from './parts/MaintenanceEditModal';
import MaintenanceCancelModal from './parts/MaintenanceCancelModal';
import { MaintenanceItem, MaintenanceItemAdd, MaintenanceItemEdit, MaintenanceStatus } from 'models/api/maintenance';
import DataTimeText from 'components/Common/DataTime';
import useApi from 'hooks/useApi';
import { getMaintenanceList, cancelMaintenance, createMaintenance, updateMaintenance, deleteMaintenance, completeMaintenance } from 'api/maintenanceApi';
import { useSearchParams } from 'react-router-dom';
import { defaultPagination } from 'utils/consts';
import { Platform } from 'models/api/platform';
import { ResponseData } from 'models/api/common';

const Maintenance: React.FC = () => {
    const [searchParams] = useSearchParams()

    const [tableData, setTableData] = useState<ResponseData<MaintenanceItem>>({ records: [], allRecordsCount: 0 });

    const filter = {    
        orderColumn: 'platform',
        orderDesc: true,
    };

    const api = useApi();

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    const fetchMaintenanceList = async () => {
        setLoading(true);

        setError(null);

        const pageSize = Number(searchParams.get('pageSize'));
        const pageNumber = Number(searchParams.get('pageNum'));

        const pagination = pageSize || pageNumber ? { pageSize, pageNumber } : defaultPagination;

        try {
            const { data } = await getMaintenanceList(api, pagination);
            setTableData(data);
        } catch (err: unknown) {
          setError((err as Error).message || 'Failed to load maintenance list');
        } finally {
          setLoading(false);
        }
    };   

    useEffect(() => {
        fetchMaintenanceList()
    }, [searchParams])

    const filterParams = {
        orderColumn: 'platform',
        orderDesc: true,
    }

    const [isCreateModalVisible, setCreateModalVisible] = useState(false);
    const [isEditModalVisible, setEditModalVisible] = useState(false);
    const [isCancelModalVisible, setCancelModalVisible] = useState(false);
    const [cancelModalTitle, setCancelModalTitle] = useState('');
    const [cancelModalText, setCancelModalText] = useState('');

    const isActiveStage = (stage: MaintenanceStatus) => {
        return stage === MaintenanceStatus.Maintenance || stage === MaintenanceStatus.Planning;
    }


    const modalTitles = {
        [MaintenanceStatus.Maintenance]: 'Завершение ТО',
        [MaintenanceStatus.Planning]: 'Отмена ТО',
        [MaintenanceStatus.Cancelled]: 'Удаление ТО',
        [MaintenanceStatus.Completed]: 'Удаление ТО',
    }

    const modalTexts = {
        [MaintenanceStatus.Maintenance]: 'Вы уверены, что хотите завершить ТО?',
        [MaintenanceStatus.Planning]: 'Вы уверены, что хотите отменить запланированное ТО?',
        [MaintenanceStatus.Cancelled]: 'Вы уверены, что хотите удалить ТО?',
        [MaintenanceStatus.Completed]: 'Вы уверены, что хотите удалить ТО?',
    }

    const [deletedRow, setDeletedRow] = useState({} as MaintenanceItem);
    const handleDeleteRow = (row: MaintenanceItem) => {
        setDeletedRow(row);

        const modalTitle = modalTitles[row.stage];
        const modalText = modalTexts[row.stage];

        setCancelModalTitle(modalTitle);
        setCancelModalText(modalText);

        setCancelModalVisible(true);
    };
    const deleteRow = async () => {
        if (deletedRow.stage === MaintenanceStatus.Maintenance) {
            await completeMaintenance(api, { platform: deletedRow.platform as Platform })
        } else if (deletedRow.stage === MaintenanceStatus.Planning) {
            await cancelMaintenance(api, deletedRow.id)
        } else {
            await deleteMaintenance(api, deletedRow.id)
        }

        await fetchMaintenanceList()
    }

    const setMaintenanceHandler = async (form: MaintenanceItemAdd) => {
        try {
            form.platform = form.platform ?? Platform.All;
            await createMaintenance(api, form);
        } catch (error) {
            console.error('Request error: ', error)
        }
        await fetchMaintenanceList()
    };

    const setEditableMaintenanceHandler = async (form: MaintenanceItemEdit) => {
        await updateMaintenance(api, form)

        await fetchMaintenanceList()
    }

    const [editableRow, setEditableRow] = useState({} as MaintenanceItem)
    const handleEditModalOpening = (row: MaintenanceItem) => {
        setEditableRow(row)

        setEditModalVisible(true);
    }

    const disabledEdition = (row: MaintenanceItem) => {
        return row.stage !== MaintenanceStatus.Planning;
    }

    const initColumns= [
        {
            key: 'maintenanceBegin',
            name: 'Дата начала',
            formatter: ({ row }: { row: MaintenanceItem }) => (
                <>
                    <div>
                        <DataTimeText value={row.maintenanceBegin} />
                    </div>
                </>
            ),
        },
        { key: 'platform', name: 'Платформа' },
        { key: 'stage', name: 'Статус' },
        {
            key: 'reason', 
            name: 'Описание',
            formatter: ({ row }: { row: MaintenanceItem }) => (
                <p>
                    { row.reason ?? '-' }
                </p>
            ),
        },
        {
            key: 'maintenanceEnd',
            name: 'Дата окончания',
            formatter: ({ row }: { row: MaintenanceItem }) => (
                <>
                    {
                        row.maintenanceEnd ? (
                            <div>
                                <DataTimeText value={row.maintenanceEnd} />
                            </div>
                        ) : '-'
                    }
                </>
            ),
        },
        {
            key: 'lastUpdate',
            name: 'Дата обновления',
            formatter: ({ row }: { row: MaintenanceItem }) => (
                <>
                    <div>
                        <DataTimeText value={row.lastUpdate} />
                    </div>
                </>
            ),
        },
        {
            key: 'actions',
            name: 'Действия',
            cellClass: 'base-data-grid__cell_centered no-change-modal',
            headerCellClass: 'base-data-grid__cell_centered',
            width: 100,
            minWidth: 100,
            draggable: false,
            resizable: false,
            clickDisable: true,
            formatter: ({ row }: { row: MaintenanceItem }) => {
                return (
                    <>
                        <MdEdit
                            size={20}
                            className={`cursor--pointer actions-icon ${disabledEdition(row) ? 'disabled' : ''}`}
                            onClick={() => handleEditModalOpening(row)}
                        />
                        {
                            isActiveStage(row.stage) ? (
                                <MdCancel
                                    size={20}
                                    className="cursor--pointer actions-icon"
                                    onClick={() => handleDeleteRow(row)}                        
                                />
                            ) : (
                                <MdDelete
                                    size={20}
                                    className="cursor--pointer actions-icon"
                                    onClick={() => handleDeleteRow(row)}
                                />
                            )
                        }
                    </>
                );
            },
        },
    ]
    
    return (
        <div className="maintenance">
            <div className="maintenance__left">
                <h1>Техническое обслуживание</h1>
                <div className='d-flex justify-content-between align-items-center my-4'>
                    <BaseButton
                        className="sidebar__button"
                        type="submit"
                        color="primary"
                        data-testid="login-button"
                        onClick={() => setCreateModalVisible(true)}
                    >
                        <span>
                            Запланировать техническое обслуживание
                        </span>
                    </BaseButton>
                </div>
            </div>
            {
                error ? (<div>Error: {error}</div>) :
                (
                    <div className="maintenance__right">
                        <GridFilterUrlProvider filterParams={filterParams}>
                            <GridColumnsConfigProvider storagePath={'/maintenance'} initColumns={initColumns}>
                                <WorkplaceTable<MaintenanceItem, MaintenanceItem>
                                    showSummaryRows
                                    lineHeight={12}
                                    rowClass={() => 'test'}
                                    clickable={false}
                                    tableData={tableData}
                                    filter={filter}
                                    tableLoading={loading}
                                >
                                    {true && (
                                        <div className="base-data-grid__footer">
                                            <BasePagination<MaintenanceItem> tableData={tableData} />
                                        </div>
                                    )}
                                </WorkplaceTable>
                            </GridColumnsConfigProvider>
                        </GridFilterUrlProvider>
                        <MaintenanceEditModal<MaintenanceItemAdd>
                            isOpen={isCreateModalVisible} 
                            toggle={setCreateModalVisible}
                            handleChange={setMaintenanceHandler}
                            defaultValuesProps={{
                                reason: '',
                                platform: null,
                                maintenanceBegin: null,
                                maintenanceEnd: null,
                            }}
                        />
                        <MaintenanceEditModal<MaintenanceItemEdit>
                            isEditing
                            isOpen={isEditModalVisible} 
                            toggle={setEditModalVisible}
                            handleChange={setEditableMaintenanceHandler}
                            editableRow={editableRow}
                            defaultValuesProps={{
                                maintenanceBegin: null,
                                maintenanceEnd: null,
                            }}                    
                        />
                        <MaintenanceCancelModal
                            isOpen={isCancelModalVisible} 
                            toggle={setCancelModalVisible}
                            handleChange={deleteRow}
                            title={cancelModalTitle}
                            text={cancelModalText}
                        />
                    </div>
                )                
            }
        </div>
    );
};

export default Maintenance;
